import React, { Component } from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Card, CardHeader, CardContent } from '@cargomatic/ui';
import { isProd } from 'helpers/helper_functions';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import SessionStore from '../../../stores/SessionStore';
import StripeAccountsStore from '../../../stores/StripeAccountsStore';
import CustomersAPIUtils from '../../../utils/CustomersAPIUtils';
import CustomersActionCreators from '../../../actions/CustomersActionCreators';
import { getAllMarkets, normalizeMarketIds } from 'utils/MarketAPIUtils';
import { showToast } from 'stores/ToastStore';
import Alert from '../../ui/Alert';
import Badge from '../../v2/Badge';
import CheckedIndicator from '../../v2/CheckedIndicator';
import Sidebar from './Sidebar';
import Container from '../../v2/container';

class CarrierDocuments extends Component {
  constructor(props) {
    super(props);
    this.state = {
      authorized: false,
      documentsValid: false,
      loading: true,
      carrier: {},
      apiError: false,
      apiErrorText: null,
      bankAccount: {},
      allMarkets: [],
    };
  }

  componentDidMount() {
    const carrier = SessionStore.getUser();
    this.setState({ carrier });
    if (carrier.dot_number && isProd()) {
      CustomersAPIUtils.getCarrierRmisDocuments(
        carrier.dot_number,
        this.setRmisStatus,
      );
    }
    SessionStore.addChangeListener(this.handleStoreChange);
    StripeAccountsStore.addChangeListener(this.handleReceiveStripeInfo);
    this.populateCarrierState();
    this.getCustomer();
    this.loadMarkets();
  }

  componentWillUnmount() {
    SessionStore.removeChangeListener(this.handleStoreChange);
    StripeAccountsStore.addChangeListener(this.handleReceiveStripeInfo);
  }

  getCustomer() {
    const user = SessionStore.getUser();
    if (user) {
      CustomersActionCreators.getCustomer(user._id, false);
      CustomersActionCreators.getBankAccounts(user._id);
    }
  }
  loadMarkets = async () => {
    try {
      const allMarkets = await getAllMarkets();
      this.setState({ allMarkets });
    } catch (e) {
      this.setState({ allMarkets: [] });
      showToast(this.props.t('LOAD_MARKETS_FAILED'), e.message, 'error');
    }
  };

  setRmisStatus = (status) => {
    if (
      status.CertificationStatus &&
      status.CertificationStatus.IsCertified === 'True'
    ) {
      this.setState({ documentsValid: true, loading: false });
    }

    if (
      status.CertificationStatus &&
      status.CertificationStatus.IsCertified === 'False'
    ) {
      this.setState({ documentsValid: false, loading: false });
    }

    if (status.Header.Result === 'ERROR') {
      this.setState({ apiError: true, loading: false });

      if (status.Header.Errors && status.Header.Errors.Error.length) {
        const apiErrorText = status.Header.Errors.Error[0];
        this.setState({ apiErrorText });
      }
    }
  };

  handleReceiveStripeInfo = () => {
    const accountInfo = StripeAccountsStore.getAccountInfo();

    if (!accountInfo) return;

    const bankAccount = (accountInfo.external_accounts || [])[0] || {
      id: null,
      bank_name: '',
      last4: '',
    };

    this.setState({
      bankAccount,
    });
  };
  handleStoreChange = () => {
    this.populateCarrierState();
  };

  populateCarrierState() {
    const carrier = SessionStore.getUser();
    if (!carrier) {
      return;
    }
    const { authorized } = carrier;

    this.setState({
      authorized,
      carrier,
    });
  }

  hasBankAccount() {
    const { id } = this.state.bankAccount;
    return id && id.length > 0;
  }

  renderPayoutValidation() {
    const completed = this.hasBankAccount();
    const { t } = this.props;
    const action = completed ? 'update' : 'add';
    const text = t('GO_TO_BILLING_AND_ACTION_YOUR_BANK', { action });
    return (
      <div className="">
        <div className="flex flex-wrap justify-between">
          <div className="flex items-center mb-4 space-x-2">
            <h3 className="my-0">{t('PAYOUT_VERIFICATION')}</h3>
            <Badge color={completed ? 'brand' : 'red'}>
              {completed ? t('COMPLETED') : t('INCOMPLETE')}
            </Badge>
          </div>
          <LinkContainer
            className="mt-2 mb-4 text-blue-700 cursor-pointer hover:text-blue-600 hover:underline"
            to="/billing"
          >
            <div>
              <span>{text}</span>
              <span className="ml-1">&rarr;</span>
            </div>
          </LinkContainer>
        </div>
        <p className="flex items-center space-x-2">
          <span>{t('BANK_ON_FILE')}</span>

          <span>
            <CheckedIndicator checked={completed} />
          </span>
        </p>
      </div>
    );
  }

  renderBankingInformation = () => {
    const verified = this.state?.carrier?.bank_verified;
    const { t } = this.props;

    return (
      <div className="">
        <div className="flex flex-wrap justify-between">
          <div className="flex items-center mb-4 space-x-2">
            <h3 className="my-0">{t('BANK_INFORMATION')}</h3>
            <Badge color={verified ? 'brand' : 'yellow'}>
              {verified ? t('VERIFIED') : t('PENDING')}
            </Badge>
          </div>
        </div>
      </div>
    );
  };

  renderLoadingIndicator() {
    const { t } = this.props;
    return (
      <div>
        <span>{t('WAITING_FOR_RMIS_STATUS')}</span>
      </div>
    );
  }

  renderRMISIndicator() {
    const { t } = this.props;
    if (this.state.loading) {
      return this.renderLoadingIndicator();
    }
    if (this.state.apiError) {
      return this.renderRMISError();
    }

    return (
      <div>
        <div className="flex flex-wrap justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="my-0">{t('RMIS_DOCUMENT_STATUS')}</h3>
            {this.state.documentsValid ? (
              <Badge color="brand">{t('VALID')}</Badge>
            ) : (
              <Badge color="red">{t('INVALID')}</Badge>
            )}
          </div>
          <a
            className="text-blue-700 hover:text-blue-600"
            href={CARRIER_RMIS_LINK}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="mr-1">{t('VIEW_DOCUMENTS_IN_RMIS')}</span>
            <span>&rarr;</span>
          </a>
        </div>
        {!this.state.documentsValid && (
          <Alert type="error" className="mt-4">
            {t('INVALID_DOCUMENT_FOUND_TIP')}
          </Alert>
        )}
      </div>
    );
  }

  // Carrier Does not have a DOT number attached to profile...
  renderDotMissingError() {
    const { t } = this.props;
    return (
      <div>
        <Alert title={t('ERROR_MISSING_DOT_NUMBER')} type="error">
          <p>{t('DOT_NUMBER_IS_REQUIRED_FOR_CARRIER_AUTHORIZATION')}</p>
          <p>{t('DOT_NUMBER_IS_REQUIRED_FOR_CARRIER_AUTHORIZATION_TIP')}</p>
          <div className="mt-4 actions">
            <LinkContainer
              to="/carrier-center"
              className="font-medium cursor-pointer hover:text-red-600 hover:underline"
            >
              <div>
                <span>{t('UPDATE_CARRIER_PROFILE')}</span>
                <span className="ml-1">&rarr;</span>
              </div>
            </LinkContainer>
          </div>
        </Alert>
      </div>
    );
  }

  renderRMISError() {
    const { t } = this.props;
    return (
      <div>
        <Alert title={t('RMIS_API_ERROR')}>
          {this.state.apiErrorText && <p>{this.state.apiErrorText}</p>}
          <p>
            {t('DOT_NUMBER_CHECK_TIP')}
            <LinkContainer to="/carrier-center">
              <div>
                <span>{t('UPDATE_CARRIER_PROFILE')}</span>
                <span className="ml-1">&rarr;</span>
              </div>
            </LinkContainer>
          </p>
        </Alert>
      </div>
    );
  }

  // Carrier has not been authorized
  renderCarrierInfo() {
    const { carrier, allMarkets } = this.state;
    const { t } = this.props;
    const carrierMarketIds = normalizeMarketIds(carrier.markets);
    const selectedMarketNames = allMarkets
      .filter((m) => carrierMarketIds.includes(m._id))
      .map((m) => m.name);
    const selectedMarkets = selectedMarketNames.length
      ? selectedMarketNames.join(', ')
      : 'None';

    return (
      <div>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <div className="sm:col-span-1 space-y-2">
            <h4 className="my-0 text-sm font-medium text-gray-500 lg:mb-0">
              {t('CURRENT_AUTHORIZATION_STATUS')}
            </h4>
            <Badge color={this.state.authorized ? 'brand' : 'red'}>
              {this.state.authorized
                ? titleize(t('AUTHORIZED'))
                : titleize(t('UNAUTHORIZED'))}
            </Badge>
          </div>
          <div className="sm:col-span-1 space-y-2">
            <h4 className="my-0 mt-4 text-sm font-medium text-gray-500 sm:mt-0">
              {t('SELECTED_MARKETS')}
            </h4>
            <span>{selectedMarkets}</span>
          </div>
        </div>
        {!this.state.authorized && (
          <div className="mt-4">
            <Alert className="my-4" type="warning" title="Almost There...">
              <p className="mb-1">
                {t('NEED_FEW_DOCUMENTS_TO_ACCEPT_SHIPMENTS')}
              </p>
              <p className="mb-1">
                {t('CLICK_BUTTON_BELOW_TO_UPDATE_DOCUMENTS_IN_RMIS')}
              </p>
              <p className="mb-1">
                {t(
                  'IF_ALREADY_UPLOADED_DOCUMENTS_IN_RMIS_CHECK_STATUS_AGAIN_IN_24_HRS',
                )}
              </p>
              <div className="actions">
                <a
                  className="mt-4 font-medium text-yellow-700 hover:text-yellow-600 whitespace-nowrap"
                  href={CARRIER_RMIS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="mr-1">{t('GO_TO_RMIS')}</span>
                  <span className="text-yellow-700" aria-hidden="true">
                    &rarr;
                  </span>
                </a>
              </div>
            </Alert>
          </div>
        )}
      </div>
    );
  }

  render() {
    const hasDOT =
      this.state.carrier.dot_number && this.state.carrier.dot_number.length;
    const { t } = this.props;
    return (
      <Container className="documents">
        <div className="lg:grid lg:grid-cols-12 lg:gap-6 mt-10">
          <div className="lg:col-span-3">
            <Sidebar activePage="documents" />
          </div>
          <div className="lg:col-span-6">
            <Card>
              <CardHeader>
                <h3 className="my-0">{t('MY_DOCUMENTS')}</h3>
              </CardHeader>
              <CardContent>
                {this.renderCarrierInfo()}
                <hr />
                <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                  {hasDOT
                    ? this.renderRMISIndicator()
                    : this.renderDotMissingError()}
                  <hr />
                  {this.renderPayoutValidation()}
                </AuthorizationWrapper>
                <AuthorizationWrapper excludedRegions={[REGIONS.US]}>
                  {this.renderBankingInformation()}
                </AuthorizationWrapper>
              </CardContent>
            </Card>
          </div>
        </div>
      </Container>
    );
  }
}

export default withTranslation()(CarrierDocuments);
